import { createRouter, createWebHistory } from 'vue-router';
import { captureException } from '@sentry/browser';
import type { Extras } from '@sentry/types/types/extra';
import { resolveSupportedLocale } from '@deepcloud/deep-ui-utils';
import AuthService from '@/services/authService.ts';
import { useErrorStore } from '@/stores/error.ts';
import { useGlobalStore } from '@/stores/global.ts';
import deepBoxDocumentGuardian from '@/router/guardians/deepBoxDocumentGuardian';
import reconnectGuardian from '@/router/guardians/reconnectGuardian';
import settingsGuardian from './guardians/settingsGuardian';
import { setPersonalAccessData } from '@/api/utils.ts';
import { SUPPORTED_LOCALES } from '@/composables/useAppLocale.ts';

// Layouts
import LayoutEmpty from '@/layouts/LayoutEmpty.vue';
import LayoutDocument from '@/layouts/LayoutDocument.vue';

const routes = [
  {
    path: '/',
    component: () => import('@/layouts/LayoutDashboard.vue'),
    children: [
      {
        path: '',
        component: () => import('@/views/DashboardView.vue'),
        name: 'dashboard'
      }
    ]
  },
  {
    path: '/batch',
    component: () => import('@/layouts/LayoutDocumentBatch.vue'),
    name: 'document-batch',
    children: [
      {
        path: '/batch/sign',
        component: () => import('@/views/DocumentBatchPage.vue'),
        name: 'document-batch-sign'
      }
    ]
  },
  {
    path: '/deepbox',
    component: LayoutDocument,
    beforeEnter: (to) => {
      const globalStore = useGlobalStore();
      globalStore.history = [];
      globalStore.addRouteToHistory(to.fullPath);
    },
    name: 'deepbox',
    children: [
      {
        path: '/deepbox/:id',
        component: LayoutEmpty,
        beforeEnter: deepBoxDocumentGuardian(),
        name: 'deepbox-id'
      },
      {
        path: '/deepbox/:id?/pending',
        component: LayoutEmpty,
        beforeEnter: deepBoxDocumentGuardian(),
        name: 'deepbox-id-pending'
      },
      {
        path: '/deepbox/:id?/signed',
        component: LayoutEmpty,
        beforeEnter: deepBoxDocumentGuardian(),
        name: 'deepbox-id-signed'
      }
    ]
  },
  {
    path: '/',
    component: LayoutDocument,
    children: [
      {
        path: '/sign/:id?',
        component: () => import('@/views/DocumentPage.vue'),
        name: 'sign-id',
        beforeEnter: async (to) => {
          if (to.params?.id && to.query?.sid) {
            const signKey = Array.isArray(to.params.id)
              ? to.params.id[0]
              : to.params.id;
            await setPersonalAccessData(signKey, to.query.sid);
          }
        },
        meta: {
          auth: false
        }
      },
      {
        path: '/document-access',
        name: 'document-access',
        component: () => import('@/views/DocumentAccessPage.vue'),
        meta: {
          auth: false
        }
      },
      {
        path: '/new-link/:id?',
        component: () => import('@/views/NewLinkPage.vue'),
        name: 'new-link',
        meta: {
          auth: false
        }
      }
    ]
  },

  {
    path: '/document',
    component: LayoutDocument,
    children: [
      {
        path: '/document/:document?/create',
        component: () => import('@/views/DocumentPage.vue'),
        name: 'document-create'
      },
      {
        path: '/document/:document?/details',
        component: () => import('@/views/DocumentPage.vue'),
        name: 'document-details',
        beforeEnter: (_to) => {
          if (_to.params?.document) {
            return true;
          } else {
            return { name: 'dashboard' };
          }
        }
      },
      {
        path: '/document/:document?/sign',
        component: () => import('@/views/DocumentPage.vue'),
        name: 'document-sign',
        beforeEnter: (_to) => {
          if (_to.params?.document) {
            return true;
          } else {
            return { name: 'dashboard' };
          }
        }
      },
      {
        path: '/document/:document?/seal',
        component: () => import('@/views/DocumentSealPage.vue'),
        name: 'document-seal'
      }
    ]
  },
  {
    path: '/p/document',
    name: 'public-document',
    component: LayoutDocument,
    meta: {
      auth: false
    },
    children: [
      {
        path: ':document?/create',
        component: () => import('@/views/DocumentPublicPage.vue'),
        name: 'p-document-create',
        meta: {
          auth: false
        }
      },
      {
        path: ':document?/view',
        component: () => import('@/views/DocumentPublicPage.vue'),
        name: 'p-document-view',
        meta: {
          auth: false
        }
      }
    ]
  },
  {
    path: '/reconnect',
    component: LayoutEmpty,
    name: 'reconnect',
    beforeEnter: reconnectGuardian(),
    meta: {
      auth: false
    }
  },
  {
    path: '/settings',
    name: 'settings',
    beforeEnter: settingsGuardian()
  },
  {
    path: '/404',
    name: 'error-page',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      auth: false
    }
  },
  {
    path: '/:catchAll(.*)*',
    alias: '/404',
    component: () => import('@/views/NotFound.vue'),
    name: '404',
    meta: {
      auth: false
    }
  }
];

// Create fn to init the router in order to solve an issue where KC query params stay on the URL
// https://github.com/dsb-norge/vue-keycloak-js/issues/94#issuecomment-1794403391
// https://github.com/keycloak/keycloak/issues/14742#issuecomment-1663069438
const initializeRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior(to) {
      if (to.params.savePosition) {
        return null;
      }
      return { x: 0, y: 0 };
    }
  });

  interface LoginOptions {
    redirectUri: string;
    locale?: string;
  }

  router.beforeEach(async (to, _from) => {
    if (AuthService.isAuthenticated && to.name !== 'settings') {
      sessionStorage.removeItem(
        `deepsign.${AuthService.user?.sub}.settings.back.route`
      );
    }

    if (!to.name?.startsWith('p-document')) {
      sessionStorage.removeItem('documentToken');
      sessionStorage.removeItem('documentId');
    }
    const errorStore = useErrorStore();
    if (errorStore.isError) {
      errorStore.clear();
    }

    const basePath = window.location.origin;
    const keycloakRedirectUri = basePath + to.fullPath;
    if (AuthService.isAuthenticated) {
      await AuthService.updateToken();
      return true;
    } else {
      if (to.meta && to.meta.auth !== false) {
        const routeLang: string = (to.query?.lang as string) || '';
        const loginOptions: LoginOptions = { redirectUri: keycloakRedirectUri };
        if (routeLang) {
          loginOptions.locale = resolveSupportedLocale(
            routeLang,
            SUPPORTED_LOCALES,
            'en'
          );
        }
        return AuthService.login(loginOptions);
      }
      return true;
    }
  });

  router.onError((error: Error) => {
    if (error.message.includes('Failed to fetch dynamically imported module')) {
      const extras: Extras = { extra: error.message };
      captureException(
        'Triggered automatic reload after failed to fetch dynamically imported module. ',
        extras
      );
      return () => {
        window.location.reload();
      };
    }
  });

  console.info('ROUTER: router initialized');

  return router;
};

export default initializeRouter;
